import type { RiskScoreWindow } from '@amzn/gbsa-helios-typescript-client';
import { Button, ButtonVariant } from '@amzn/stencil-react-components/button';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { H1, Text } from '@amzn/stencil-react-components/text';
import { IconDownloadSmall } from '@amzn/stencil-react-icons';

import { useMessages } from 'src/v2/shared/i18n/useMessages';
import { joinMetaParts } from '../logic/riskScorePresentation';
import { RISK_ENGINE_BUNDLE, type RiskEngineMessageKey } from '../translations';
import { RISK_ENGINE_TEST_IDS } from '../testIds';
import { RiskTimeWindowToggle } from './RiskTimeWindowToggle';

export interface BreakdownExportControl {
  readonly disabled: boolean;
  readonly isExporting: boolean;
  readonly onExport: () => void;
}

interface BreakdownHeaderProps {
  readonly station: string;
  readonly metricGroupId: string;
  /** The root KPI's display name; the group id stands in until the group loads. */
  readonly metricName: string | undefined;
  readonly businessUnit: string | undefined;
  readonly scoringDate: string | undefined;
  readonly timeWindow: RiskScoreWindow;
  readonly onWindowChange: (window: RiskScoreWindow) => void;
  /** Desktop-only CSV export; omit to hide the button (mobile). */
  readonly exportControl?: BreakdownExportControl;
}

/**
 * The breakdown page's header: the "{station} — {group} Impact Breakdown" title with a single
 * metadata line under it (the question this page answers · business unit · scored date), and on
 * the right the Export button with the scoring-window toggle stacked beneath it.
 */
export function BreakdownHeader({
  station,
  metricGroupId,
  metricName,
  businessUnit,
  scoringDate,
  timeWindow,
  onWindowChange,
  exportControl,
}: BreakdownHeaderProps): JSX.Element {
  const { t, f } = useMessages<RiskEngineMessageKey>(RISK_ENGINE_BUNDLE);

  const subtitle = joinMetaParts([
    f('breakdownQuestion', { metric: metricName ?? metricGroupId }),
    businessUnit,
    scoringDate ? f('scoredDate', { date: scoringDate }) : undefined,
  ]);

  // Full-width row: the title block on the left, Export + window toggle pushed to the right edge.
  return (
    <Row
      alignItems="flex-start"
      justifyContent="space-between"
      gridGap="dimensions.spacing.300"
      width="100%"
    >
      <Col gridGap="dimensions.spacing.50" minWidth={0}>
        <H1>{f('breakdownTitle', { station, group: metricGroupId })}</H1>
        <Text color="color.text.secondary">{subtitle}</Text>
      </Col>
      <Col alignItems="flex-end" gridGap="dimensions.spacing.200" flexShrink={0}>
        {exportControl ? (
          <Button
            variant={ButtonVariant.Secondary}
            icon={<IconDownloadSmall aria-hidden />}
            disabled={exportControl.disabled || exportControl.isExporting}
            onClick={exportControl.onExport}
            dataTestId={RISK_ENGINE_TEST_IDS.breakdownExport}
          >
            {exportControl.isExporting ? t('exportInProgress') : t('exportButton')}
          </Button>
        ) : null}
        <RiskTimeWindowToggle value={timeWindow} onChange={onWindowChange} />
      </Col>
    </Row>
  );
}
